import { supabase } from "./supabase.js";

/* Web Push for bill reminders.
 *
 * Aurio has had bill reminders since v0.13 but they fired from `new
 * Notification()` while the app was OPEN — which is exactly not when a
 * reminder is useful, and the feature is sold as Premium. This is the half
 * that makes the promise true: the browser registers a push endpoint, the
 * endpoint is stored against auth.uid(), and a daily Edge Function
 * (send-reminders) pushes to it whether or not the app is running.
 *
 * Everything here degrades to false rather than throwing. If VAPID isn't
 * configured, Supabase isn't set up, the browser lacks PushManager, or the
 * user denies permission, the app keeps its existing on-open notifications
 * and simply never promises more than it can deliver.
 *
 * Requires VITE_VAPID_PUBLIC_KEY at build time, matching the key pair the
 * Edge Function signs with. */

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

/* The applicationServerKey must be a Uint8Array of the raw bytes; VAPID keys
   are distributed URL-safe base64, which atob() will not accept as-is. */
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normal = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normal);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && Boolean(VAPID_PUBLIC_KEY)
    && Boolean(supabase);
}

export async function getPushSubscription() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/* Returns true only when the endpoint actually reached the database. A
   subscription the server doesn't know about would leave the toggle claiming
   reminders are on while nothing could ever be sent — the same class of lie as
   the cloud-backup button that reported success without writing (v0.35.1). */
export async function subscribeToPush() {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    const json = sub.toJSON();
    const { error } = await supabase.rpc("upsert_push_subscription", {
      p_endpoint: json.endpoint,
      p_p256dh: json.keys.p256dh,
      p_auth: json.keys.auth,
    });
    if (error) throw error;
    return true;
  } catch (err) {
    console.warn("[Aurio] push subscribe failed", err);
    return false;
  }
}

/* Best-effort on both sides: drop the row so the server stops sending, and
   unsubscribe locally so the browser stops holding an endpoint we no longer
   use. A failure on either half must not block turning reminders off. */
export async function unsubscribeFromPush() {
  try {
    const sub = await getPushSubscription();
    if (!sub) return true;
    if (supabase) {
      try {
        await supabase.rpc("delete_push_subscription", { p_endpoint: sub.endpoint });
      } catch { /* the local unsubscribe below still stops delivery */ }
    }
    await sub.unsubscribe();
    return true;
  } catch (err) {
    console.warn("[Aurio] push unsubscribe failed", err);
    return false;
  }
}
